import { differenceInYears, isFuture, isValid } from "date-fns";
import { z } from "zod";

import { FILL_OPTION } from "~/services/scheduling/constants";
import { RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS } from "../components/VisitForm/constants";
import { parseStrictDate } from "../utils/format-dob";

const YEARS_OLD_MIN = 18;
const YEARS_AGO_LIMIT = 120;
const NAME_LETTERS_MIN = 2;

const countLetters = (value: string) => (value.match(/[a-zA-Z]/g) ?? []).length;

const NAME_PATTERN = /^[a-zA-Z'-]+[a-zA-Z\s'-]*$/;
const STARTS_WITH_LETTER = /^[a-zA-Z]/;
const HAS_LETTER = /[a-zA-Z]/;
const HAS_LETTER_OR_NUMBER = /[a-zA-Z0-9]/;
const NORTH_AMERICAN_PHONE_FORMAT = /^[2-9]\d{2}[2-9]\d{6}$/;
const FICTIONAL_PHONE_RANGE = /^\d{3}55501\d{2}$/;

const isNorthAmericanPhone = (value: string) => {
  const digits = value.replace(/\D/g, "");

  return (
    NORTH_AMERICAN_PHONE_FORMAT.test(digits) &&
    !FICTIONAL_PHONE_RANGE.test(digits)
  );
};

const isEmptyOrMatches = (pattern: RegExp) => (value?: string | null) =>
  !value || pattern.test(value);

const withNameRules = (schema: z.ZodString, label: string) =>
  schema
    .refine(
      isEmptyOrMatches(STARTS_WITH_LETTER),
      `${label} must start with a letter`,
    )
    .refine(
      isEmptyOrMatches(NAME_PATTERN),
      `${label} must contain only letters, spaces, apostrophes, or hyphens`,
    );

const addressLineMessage = "Address must contain at least one letter or number";
const cityMessage = "City must contain at least one letter";

const dateOfBirthSchema = z
  .string({ error: "Date of birth is required" })
  .min(1, "Date of birth is required")
  .refine(
    (date) => isValid(parseStrictDate(date)!),
    "Please enter a valid date",
  )
  .refine(
    (date) => !isFuture(parseStrictDate(date)!),
    "Date of birth cannot be in the future",
  )
  .refine(
    (date) =>
      differenceInYears(new Date(), parseStrictDate(date)!) >= YEARS_OLD_MIN,
    `You must be at least ${YEARS_OLD_MIN} years old to use this service`,
  )
  .refine(
    (date) =>
      differenceInYears(new Date(), parseStrictDate(date)!) <= YEARS_AGO_LIMIT,
    `Date of birth cannot be more than ${YEARS_AGO_LIMIT} years ago`,
  );

// Base personal info without insurance
export const basePersonalInformationObject = z.object({
  firstName: withNameRules(
    z
      .string({ error: "First name is required" })
      .min(1, "First name is required")
      .max(20, "First name must be less than 20 characters"),
    "First name",
  ).refine(
    (value) => countLetters(value) >= NAME_LETTERS_MIN,
    `First name must be at least ${NAME_LETTERS_MIN} characters`,
  ),

  lastName: withNameRules(
    z
      .string({ error: "Last name is required" })
      .min(1, "Last name is required")
      .max(20, "Last name must be less than 20 characters"),
    "Last name",
  ).refine(
    (value) => countLetters(value) >= NAME_LETTERS_MIN,
    `Last name must be at least ${NAME_LETTERS_MIN} characters`,
  ),

  dateOfBirth: dateOfBirthSchema,

  sexAtBirth: z
    .string()
    .refine((v) => ["M", "F"].includes(v), "Please select a valid option"),

  mobilePhone: z
    .string({ error: "Mobile phone number is required" })
    .min(1, "Mobile phone number is required")
    .regex(
      /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
      "Please enter mobile number with format: (123) 456-7890",
    )
    .refine(isNorthAmericanPhone, "Please enter a valid US phone number"),

  homeAddress1: z
    .string({ error: "Address is required" })
    .min(1, "Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(100, "Address must be less than 100 characters")
    .regex(HAS_LETTER_OR_NUMBER, addressLineMessage),
  homeAddress2: z
    .string()
    .max(100)
    .refine(isEmptyOrMatches(HAS_LETTER_OR_NUMBER), addressLineMessage)
    .optional()
    .nullable(),

  city: z
    .string({ error: "City is required" })
    .min(1, "City is required")
    .min(2, "City must be at least 2 characters")
    .max(30, "City must be less than 30 characters")
    .regex(HAS_LETTER, cityMessage),
  state: z.string({ error: "State is required" }).min(1, "State is required"),
  zipCode: z
    .string({ error: "Zip code is required" })
    .min(1, "Zip code is required")
    .regex(/^\d{5}(-\d{4})?$/, "Please enter a valid US zip code"),
});

// Demographics screen (Nirvana flow) reuses the same field-level validations
// as the Personal Information general info section.
export const demographicsSchema = basePersonalInformationObject.pick({
  firstName: true,
  lastName: true,
  dateOfBirth: true,
  sexAtBirth: true,
  zipCode: true,
});

export type DemographicsFormData = z.infer<typeof demographicsSchema>;

export const optionalAddressSchema = z.object({
  homeAddress1: z
    .string()
    .max(100, "Address must be less than 100 characters")
    .refine(isEmptyOrMatches(HAS_LETTER_OR_NUMBER), addressLineMessage)
    .optional(),
  city: z
    .string()
    .max(30, "City must be less than 30 characters")
    .refine(isEmptyOrMatches(HAS_LETTER), cityMessage)
    .optional(),
  state: z.string().optional(),
  zipCode: z
    .string()
    .refine(
      (val) => !val || /^\d{5}(-\d{4})?$/.test(val),
      "Please enter a valid US zip code",
    )
    .optional(),
});

const referralSchema = z.object({
  referralSource: z.string().optional(),
});
const privacyConsentSchema = z.object({
  privacyPolicyConsent: z
    .boolean({
      error:
        "You must agree to the Patient Services Agreement and Terms of Use",
    })
    .refine(
      (v) => v === true,
      "You must agree to the Patient Services Agreement and Terms of Use",
    ),
});

const fileSchema = z
  .instanceof(File)
  .refine(
    (file) => file.size <= 5 * 1024 * 1024,
    "File size must be less than 5MB",
  )
  .refine(
    (file) => file.type.startsWith("image/"),
    "Please upload an image file",
  )
  .nullable()
  .optional();

const addressLaterSchema = z
  .object({
    fillAddressInfo: z.literal(FILL_OPTION.LATER),
  })
  .passthrough();

const addressNowSchema = z.object({
  fillAddressInfo: z.literal(FILL_OPTION.NOW),
  homeAddress1: z
    .string({ error: "Address is required" })
    .min(1, "Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(100, "Address must be less than 100 characters")
    .regex(HAS_LETTER_OR_NUMBER, addressLineMessage),
  homeAddress2: z
    .string()
    .max(100)
    .refine(isEmptyOrMatches(HAS_LETTER_OR_NUMBER), addressLineMessage)
    .optional()
    .nullable(),
  city: z
    .string({ error: "City is required" })
    .min(1, "City is required")
    .min(2, "City must be at least 2 characters")
    .max(30, "City must be less than 30 characters")
    .regex(HAS_LETTER, cityMessage),
  state: z.string({ error: "State is required" }).min(1, "State is required"),
  zipCode: z
    .string({ error: "Zip code is required" })
    .min(1, "Zip code is required")
    .regex(/^\d{5}(-\d{4})?$/, "Please enter a valid US zip code"),
});

const addressFields = z.discriminatedUnion(
  "fillAddressInfo",
  [addressLaterSchema, addressNowSchema],
  { error: "Please select an option" },
);

export const insuranceLaterSchema = z
  .object({
    fillInsuranceInfo: z.literal(FILL_OPTION.LATER),
  })
  .passthrough();

export const insuranceNowSchema = z.object({
  fillInsuranceInfo: z.literal(FILL_OPTION.NOW),
  relationshipToPolicyHolder: z.string({
    error: "Please select your relationship to the policy holder",
  }),
  memberId: z
    .string({ error: "Member ID is required" })
    .min(1, "Member ID is required")
    .min(5, "Member ID must be at least 5 characters")
    .max(20, "Member ID must be less than 20 characters")
    .regex(
      HAS_LETTER_OR_NUMBER,
      "Member ID must contain at least one letter or number",
    ),
  policyHolderFirstName: withNameRules(z.string(), "Policy holder first name")
    .nullable()
    .optional(),
  policyHolderLastName: withNameRules(z.string(), "Policy holder last name")
    .nullable()
    .optional(),
  policyHolderDateOfBirth: dateOfBirthSchema.nullable().optional(),
  insuranceCompanyName: z
    .string()
    .max(50, "Insurance company name must be less than 50 characters")
    .refine(
      (value) => !value || value.length >= 2,
      "Insurance company name must be at least 2 characters",
    )
    .refine(
      isEmptyOrMatches(HAS_LETTER_OR_NUMBER),
      "Insurance company name must contain at least one letter or number",
    )
    .optional(),
  insuranceId: z.number().optional(),
  insuranceCardFront: fileSchema,
  insuranceCardBack: fileSchema,
});

const validateInsuranceNowFields = (
  data: Omit<z.infer<typeof insuranceNowSchema>, "fillInsuranceInfo">,
  ctx: z.RefinementCtx,
) => {
  const isNotSelf =
    data.relationshipToPolicyHolder !==
    RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS.SELF;

  if (isNotSelf) {
    if (!data.policyHolderFirstName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Policy holder first name is required",
        path: ["policyHolderFirstName"],
      });
    }
    if (!data.policyHolderLastName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Policy holder last name is required",
        path: ["policyHolderLastName"],
      });
    }
    if (!data.policyHolderDateOfBirth) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Policy holder date of birth is required",
        path: ["policyHolderDateOfBirth"],
      });
    }
  }

  if (!data.insuranceCardFront) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please upload the front of your insurance card",
      path: ["insuranceCardFront"],
    });
  }
  if (!data.insuranceCardBack) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please upload the back of your insurance card",
      path: ["insuranceCardBack"],
    });
  }
};

export const insuranceFields = z
  .discriminatedUnion(
    "fillInsuranceInfo",
    [insuranceLaterSchema, insuranceNowSchema],
    { error: "Please select an option" },
  )
  .superRefine((data, ctx) => {
    if (data.fillInsuranceInfo === FILL_OPTION.LATER) return;
    validateInsuranceNowFields(data, ctx);
  });

export const requiredInsuranceSchema = insuranceNowSchema
  .omit({ fillInsuranceInfo: true })
  .superRefine(validateInsuranceNowFields);

const initialVisitObject = basePersonalInformationObject
  .extend(referralSchema.shape)
  .extend(privacyConsentSchema.shape);

const registeredInitialVisitBase = initialVisitObject.omit({
  firstName: true,
  lastName: true,
  dateOfBirth: true,
  sexAtBirth: true,
  mobilePhone: true,
  referralSource: true,
});

export const unregisteredInitialVisitSchema = z.intersection(
  initialVisitObject,
  requiredInsuranceSchema,
);

export const registeredInitialVisitSchema = z.intersection(
  registeredInitialVisitBase,
  requiredInsuranceSchema,
);

const unregisteredInitialVisitWithOptionalInsuranceSchema = z.intersection(
  initialVisitObject,
  insuranceFields,
);

const registeredInitialVisitWithOptionalInsuranceSchema = z.intersection(
  registeredInitialVisitBase,
  insuranceFields,
);

export type UnregisteredInitialVisitWithOptionalInsuranceFormData = z.infer<
  typeof unregisteredInitialVisitWithOptionalInsuranceSchema
>;
export type RegisteredInitialVisitWithOptionalInsuranceFormData = z.infer<
  typeof registeredInitialVisitWithOptionalInsuranceSchema
>;

export const buildInitialVisitSchema = (
  registered: boolean,
  hasInsurance: boolean,
) => {
  if (hasInsurance) {
    return registered
      ? registeredInitialVisitWithOptionalInsuranceSchema
      : unregisteredInitialVisitWithOptionalInsuranceSchema;
  }
  return registered ? registeredInitialVisitBase : initialVisitObject;
};

export const followUpVisitSchema = z.intersection(
  z.intersection(privacyConsentSchema, insuranceFields),
  addressFields,
);

const coachVisitObject =
  basePersonalInformationObject.merge(privacyConsentSchema);

const registeredCoachVisitBase = coachVisitObject.omit({
  firstName: true,
  lastName: true,
  dateOfBirth: true,
  sexAtBirth: true,
  mobilePhone: true,
});

export const coachVisitSchema = z.intersection(
  coachVisitObject,
  requiredInsuranceSchema,
);

export const registeredCoachVisitSchema = z.intersection(
  registeredCoachVisitBase,
  requiredInsuranceSchema,
);

export const buildCoachVisitSchema = (
  registered: boolean,
  requireInsurance: boolean,
) => {
  if (requireInsurance) {
    return registered ? registeredCoachVisitSchema : coachVisitSchema;
  }
  return registered ? registeredCoachVisitBase : coachVisitObject;
};

export type UnregisteredInitialVisitFormData = z.infer<
  typeof unregisteredInitialVisitSchema
>;
export type RegisteredInitialVisitFormData = z.infer<
  typeof registeredInitialVisitSchema
>;
export type UnregisteredInitialVisitSelfPayFormData = z.infer<
  typeof initialVisitObject
>;
export type RegisteredInitialVisitSelfPayFormData = z.infer<
  typeof registeredInitialVisitBase
>;

export type PersonalInformationFormData =
  | UnregisteredInitialVisitFormData
  | RegisteredInitialVisitFormData
  | UnregisteredInitialVisitSelfPayFormData
  | RegisteredInitialVisitSelfPayFormData
  | UnregisteredInitialVisitWithOptionalInsuranceFormData
  | RegisteredInitialVisitWithOptionalInsuranceFormData
  | CoachVisitFormData
  | RegisteredCoachVisitFormData
  | CoachVisitSelfPayFormData
  | RegisteredCoachVisitSelfPayFormData;
export type FollowUpVisitFormData = z.infer<typeof followUpVisitSchema>;
export type CoachVisitFormData = z.infer<typeof coachVisitSchema>;
export type RegisteredCoachVisitFormData = z.infer<
  typeof registeredCoachVisitSchema
>;
export type CoachVisitSelfPayFormData = z.infer<typeof coachVisitObject>;
export type RegisteredCoachVisitSelfPayFormData = z.infer<
  typeof registeredCoachVisitBase
>;

export const appointmentDetailsSchema = z.object({
  providerName: z.string().min(1, "Clinician name is required"),
  providerTitle: z.string().min(1, "Clinician title is required"),
  date: z.string().min(1, "Date is required"),
  time: z.string().min(1, "Time is required"),
  timezone: z.string().min(1, "Timezone is required"),
});

export type AppointmentDetailsData = z.infer<typeof appointmentDetailsSchema>;
